import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

import { useMe, useSwitchOrganisation } from "../api/auth";

/**
 * Dropdown shown in the header when the profile belongs to >1 orgs.
 * Single-org case renders just a static badge (no toggle, no chevron).
 */
export function OrganisationSwitcher() {
  const me = useMe();
  const switchOrg = useSwitchOrganisation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!me.data) return null;
  const all = me.data.organisations;
  const current = me.data.organisation;

  if (all.length <= 1) {
    return (
      <span className="flex items-center gap-2 text-sm">
        <span className="h-3 w-3 rounded-full bg-chip-green" aria-hidden />
        <span className="text-muted dark:text-muted-dark max-w-[5rem] sm:max-w-none truncate">{current.name}</span>
      </span>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-sm hover:text-ink dark:hover:text-ink-dark"
      >
        <span className="h-3 w-3 rounded-full bg-chip-green" aria-hidden />
        <span className="text-muted dark:text-muted-dark max-w-[5rem] sm:max-w-none truncate">{current.name}</span>
        <ChevronDown className="h-3 w-3 text-muted dark:text-muted-dark" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-surface-dark rounded-xl ring-1 ring-black/10 dark:ring-white/10 shadow-lg p-1 z-30">
          {all.map((o) => (
            <button
              key={o.id}
              onClick={() => {
                switchOrg.mutate(o.id);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm rounded hover:bg-canvas dark:hover:bg-canvas-dark flex items-center gap-2"
            >
              <span className="flex-1 truncate">{o.name}</span>
              {o.id === current.id && <Check className="h-3.5 w-3.5 text-ink dark:text-ink-dark" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
